import React, { useMemo, useState } from 'react';
import { useAppState } from '@unfold/provider';
import {
  PreferencesWrapper,
  FormRow,
  FormLabel,
  FormWrapper,
  FormError,
  AvatarContainer,
  AvatarLabel,
  AvatarButtonsContainer,
  AvatarImage,
} from './styled';
import AvatarPicker from './AvatarPicker';
import { Typography, Link } from '@unfold/typography';
import { Input } from '@unfold/input';
import { Button } from '@unfold/button';
import { validateAlphaDash } from '@unfold/utils';
import { Toggle } from '@unfold/toggle';
import { Colors, Theme } from '@unfold/theme';
import { format, fromUnixTime } from 'date-fns';

export const Preferences: React.FC = () => {
  const [state, actions] = useAppState();
  const userFullName = useMemo(
    () => `${state.user?.name_first} ${state.user?.name_last}`,
    [state.user?.name_first]
  );
  const memberSince = useMemo(() => {
    const locale = window.navigator.language;
    const isUS = locale === 'en-US';
    const createdAt = state.user?.created_at ?? 0;
    return isUS
      ? format(fromUnixTime(createdAt), 'MMM dd, yyyy')
      : format(fromUnixTime(createdAt), 'dd/MM/yyyy');
  }, [state.user?.created_at]);

  const [brandName, setBrandName] = useState<string>(
    state.user?.brand_name ?? ''
  );

  const handleChangeBrandName = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setBrandName(e.target.value);
  };

  const handleSetBrandName = (): void => {
    actions.setBrandName({ name: brandName });
  };

  const [brandHandle, setBrandHandle] = useState<string>(
    state.user?.brand_handle ?? ''
  );

  const [brandHandleError, setBrandHandleError] = useState<string>();

  const handleChangeBrandHandle = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setBrandHandle(e.target.value);
  };

  const handleSetBrandHandle = (): void => {
    const errorMessage = validateAlphaDash(
      brandHandle,
      'The username may only contain letters, numbers, and dashes.'
    );
    if (errorMessage) {
      setBrandHandleError(errorMessage);
      return;
    }
    actions.setBrandHandle({ handle: brandHandle });
    setBrandHandleError('');
  };

  const handleAvatarUpload = (file: File): void => {
    actions.setProfilePicture(file);
  };

  const handleAvatarRemove = (): void => {
    actions.removeProfilePicture();
  }

  // const handleAvatarReplace = (): void => {
  //   // actions.setProfilePicture(file);
  // }

  // const handleChangeTheme = (): void => {
  //   const newTheme = state.theme === Theme.LIGHT ? Theme.DARK : Theme.LIGHT;
  //   actions.setTheme(newTheme);
  // };

  return (
    <PreferencesWrapper>
      <Typography variant="H700">Preferences</Typography>
      <FormWrapper>
        <AvatarContainer>
          {state.user?.profilePicture || state.user?.profilePicture !== '' ?
            <AvatarImage src={state.user?.profilePicture} /> :
            <AvatarPicker onUpload={handleAvatarUpload} />
          }
          <div>
            <AvatarLabel>Profile picture</AvatarLabel>
            {state.user?.profilePicture &&
              <AvatarButtonsContainer>
                {/* <Button
                  appearance="bordered"
                  width={100}
                  onClick={handleAvatarReplace}
                >
                  Replace
                </Button> */}
                <Button
                  appearance="bordered"
                  width={100}
                  onClick={handleAvatarRemove}
                >
                  Remove
                </Button>
              </AvatarButtonsContainer>
            }
          </div>
        </AvatarContainer>
        <FormRow>
          <FormLabel>Brand name</FormLabel>
          <Input
            appearance="bordered"
            value={brandName}
            onChange={handleChangeBrandName}
            componentAfter={
              brandName !== state.user?.brand_name ? (
                <Button
                  appearance="subtle"
                  fontWeight={500}
                  height={25}
                  onClick={handleSetBrandName}
                >
                  Save
                </Button>
              ) : null
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel>Username</FormLabel>
          <Input
            componentBefore="@"
            appearance="bordered"
            value={brandHandle}
            onChange={handleChangeBrandHandle}
            componentAfter={
              brandHandle !== state.user?.brand_handle ? (
                <Button
                  appearance="subtle"
                  fontWeight={500}
                  height={25}
                  onClick={handleSetBrandHandle}
                >
                  Save
                </Button>
              ) : null
            }
          />
          {brandHandleError && brandHandle !== state.user?.brand_handle && (
            <FormError>{brandHandleError}</FormError>
          )}
        </FormRow>
        <FormRow>
          <FormLabel>Name</FormLabel>
          <Input isDisabled value={userFullName} />
        </FormRow>
        <FormRow>
          <FormLabel>Email</FormLabel>
          <Input isDisabled value={state.user?.email} />
        </FormRow>
        <FormRow>
          <FormLabel>Subscription details</FormLabel>
          <Input
            isDisabled
            value={memberSince}
            inputAlign="right"
            componentBefore="Member since"
          />
        </FormRow>
        {/* <FormRowInline>
          <FormLabel>Dark Mode</FormLabel>
          <Toggle checked={state.theme === Theme.DARK} onChange={handleChangeTheme} />
        </FormRowInline> */}
      </FormWrapper>
      <Typography variant="Paragraph" customColor={{ light: Colors.G300, dark: Colors.G600}} css={{maxWidth: 380}}>
        <Link href="https://account.squarespace.com">Click here</Link> if you want to edit the details of your Squarespace account.
      </Typography>
    </PreferencesWrapper>
  );
};
